import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  addTimedBanner,
  editQuickOrderItemQuantity,
  getCartDataSummary,
} from '../../../store/actions/storefrontActions';
import { clearQuickOrderQuantitySaveSuccess } from '../../../store/slices/storefrontSlice';
import {
  getAllLocationsQuantity,
  getComparableQuickOrderQuantityValue,
  getLocationQuantity,
  getQuickOrderCurrentItem,
  getQuickOrderItemAlert,
} from '../../components/QuickOrderTable/quickOrderTableUtils';
import { useStorefrontState } from '../../../hooks/useReduxState';
import { getQuickOrderEditKey } from '../../utils';
import { scheduleQuickOrderCartSummaryRefresh } from './scheduleQuickOrderCartSummaryRefresh';

const QUANTITY_SAVE_SUCCESS_DISPLAY_MS = 3000;

const useQuickOrderQuantityCell = ({
  isAllLocations,
  item,
  itemAlert: itemAlertProp,
  location,
  quantityCellStateRef,
}) => {
  const dispatch = useDispatch();
  const { quickOrderData, vendor } = useStorefrontState();
  const {
    pendingQuantityEdits,
    quantitySaveSuccesses,
    quickOrderItemAlerts,
    selectedLocations,
  } = quickOrderData;
  const { settings, slug } = vendor;
  const { batchQueuesEnabled } = settings;
  const currentItem = getQuickOrderCurrentItem({ batchQueuesEnabled, item });
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [isUpdatingQuantity, setIsUpdatingQuantity] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const quantityInputRef = useRef(null);
  const skipNextBlurRef = useRef(false);
  const lastSubmittedValueRef = useRef(null);
  const quantityCellState = quantityCellStateRef.current;

  const itemAlert =
    itemAlertProp ||
    getQuickOrderItemAlert({
      cartItemId: item.cartItemId,
      isAllLocations,
      locationId: location?.id,
      quickOrderItemAlerts,
    });

  useEffect(() => {
    if (!itemAlert) {
      setTooltipOpen(false);
    }
  }, [itemAlert]);

  const selectedLocationsIds = useMemo(
    () =>
      selectedLocations.map((selectedLocation) => Number(selectedLocation.id)),
    [selectedLocations]
  );

  const editLocationsIds = useMemo(
    () => (isAllLocations ? selectedLocationsIds : [Number(location.id)]),
    [isAllLocations, location, selectedLocationsIds]
  );

  const initialValue = isAllLocations
    ? getAllLocationsQuantity({ currentItem, selectedLocations })
    : getLocationQuantity({ currentItem, locationId: location.id });

  const editKey = useMemo(
    () => getQuickOrderEditKey({ editLocationsIds, item }),
    [editLocationsIds, item]
  );
  const draftValue =
    quantityCellState.activeInputKey === editKey
      ? quantityCellState.drafts[editKey]
      : undefined;
  const [quantityValue, setQuantityValue] = useState(
    draftValue === undefined ? initialValue : draftValue
  );
  const syncedQuantityValueRef = useRef(
    getComparableQuickOrderQuantityValue(initialValue)
  );
  const isQuantityInputFocusedRef = useRef(false);

  const allLocationsEditKey = useMemo(
    () =>
      getQuickOrderEditKey({
        editLocationsIds: selectedLocationsIds,
        item,
      }),
    [item, selectedLocationsIds]
  );

  const isQuantityPending = Boolean(
    isUpdatingQuantity ||
      pendingQuantityEdits?.[editKey] ||
      (!isAllLocations && pendingQuantityEdits?.[allLocationsEditKey])
  );
  const successfulQuantityValue = quantitySaveSuccesses?.[editKey];
  const showSuccessAdornment =
    !isQuantityPending &&
    successfulQuantityValue !== undefined &&
    (successfulQuantityValue ===
      getComparableQuickOrderQuantityValue(quantityValue) ||
      successfulQuantityValue ===
        getComparableQuickOrderQuantityValue(initialValue));

  useEffect(() => {
    if (!quantitySaveSuccesses?.[editKey]) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      dispatch(clearQuickOrderQuantitySaveSuccess({ editKey }));
    }, QUANTITY_SAVE_SUCCESS_DISPLAY_MS);

    return () => window.clearTimeout(timeoutId);
  }, [dispatch, editKey, quantitySaveSuccesses?.[editKey]]);

  useLayoutEffect(() => {
    const input = quantityInputRef.current;

    if (
      quantityCellState.activeInputKey === editKey &&
      input &&
      document.activeElement !== input &&
      !input.disabled
    ) {
      input.focus();
    }
  }, [editKey, isQuantityPending, quantityCellState]);

  const commitQuantity = (rawValue) => {
    const numericValue = Number(rawValue || 0);

    if (
      Number(numericValue) === Number(initialValue) ||
      lastSubmittedValueRef.current === `${numericValue}`
    ) {
      return;
    }

    lastSubmittedValueRef.current = `${numericValue}`;
    dispatch(clearQuickOrderQuantitySaveSuccess({ editKey }));
    setIsUpdatingQuantity(true);

    dispatch(
      editQuickOrderItemQuantity({
        batchQueuesEnabled,
        displayedLocationsIds: selectedLocationsIds,
        editLocationsIds,
        item,
        quantity: numericValue,
        slug,
      })
    )
      .unwrap()
      .then(() => {
        scheduleQuickOrderCartSummaryRefresh(
          dispatch,
          slug,
          getCartDataSummary
        );
      })
      .catch(() => {
        lastSubmittedValueRef.current = null;
      })
      .finally(() => {
        setIsUpdatingQuantity(false);
      });
  };

  const handleQuickOrderRefresh = () => {
    setIsRefreshing(true);
    dispatch(
      editQuickOrderItemQuantity({
        batchQueuesEnabled,
        displayedLocationsIds: selectedLocationsIds,
        editLocationsIds,
        item,
        isRefresh: true,
        slug,
      })
    )
      .unwrap()
      .catch(() => {
        dispatch(
          addTimedBanner({
            id:
              `${item.cartItemId}-${location?.id || 'all'}` +
              '-quick-order-refresh-failed',
            message: 'Unable to recheck inventory. Please try again.',
            severity: 'error',
          })
        );
      })
      .finally(() => {
        setIsRefreshing(false);
      });
  };

  useEffect(() => {
    const nextInitialValue = getComparableQuickOrderQuantityValue(initialValue);
    const currentDraftValue = quantityCellState.drafts[editKey];
    const hasFocusedDraft =
      isQuantityInputFocusedRef.current &&
      currentDraftValue !== undefined &&
      getComparableQuickOrderQuantityValue(currentDraftValue) !==
        syncedQuantityValueRef.current;

    syncedQuantityValueRef.current = nextInitialValue;
    lastSubmittedValueRef.current = null;

    if (!hasFocusedDraft) {
      delete quantityCellState.drafts[editKey];
      setQuantityValue(initialValue);
    }
  }, [editKey, initialValue, quantityCellState]);

  const handleQuantityBlurCapture = (e) => {
    if (!e.relatedTarget || !e.currentTarget.contains(e.relatedTarget)) {
      isQuantityInputFocusedRef.current = false;
      quantityCellState.activeInputKey = null;
    }
  };

  const handleQuantityFocusCapture = (e) => {
    isQuantityInputFocusedRef.current = true;
    quantityCellState.activeInputKey = editKey;
    e.target.select();
  };

  const handleQuantityBlur = (e) => {
    if (skipNextBlurRef.current) {
      skipNextBlurRef.current = false;
      return;
    }
    commitQuantity(e.target.value);
  };

  const handleQuantityChange = (e) => {
    quantityCellState.drafts[editKey] = e.target.value;
    dispatch(clearQuickOrderQuantitySaveSuccess({ editKey }));
    setQuantityValue(e.target.value);
  };

  const handleQuantityKeyDown = (e) => {
    if (e.key === 'Enter') {
      skipNextBlurRef.current = true;
      commitQuantity(e.target.value);
    }
  };

  return {
    alertIsError: itemAlert?.level === 'error',
    alertIsStockIssue:
      itemAlert?.message?.toLowerCase().includes('out of stock') ||
      itemAlert?.message?.toLowerCase().includes('low stock'),
    batchQueuesEnabled,
    itemAlert,
    handleQuickOrderRefresh,
    handleQuantityBlur,
    handleQuantityBlurCapture,
    handleQuantityChange,
    handleQuantityFocusCapture,
    handleQuantityKeyDown,
    isQuantityPending,
    isRefreshing,
    quantityCellColumnId: isAllLocations ? 'all' : String(location.id),
    quantityCellRowId: String(item.cartItemId),
    quantityInputRef,
    quantityValue,
    setTooltipOpen,
    showSuccessAdornment,
    tooltipOpen,
  };
};

export default useQuickOrderQuantityCell;
